import React from "react";

/**
 * SwiftCash brand mark — an elite geometric monogram.
 * A tight two-tone rounded square with a fluid "S" stroke rendered as a
 * smooth cubic Bezier in emerald gradient, offset by a single accent dot.
 * Scales cleanly at 20–80px.
 *
 * Variants:
 *   default          → dark tile mark, use on light or dark backgrounds
 *   solid-emerald    → emerald tile, use on dark/navy backgrounds (legacy fallback)
 */
export function LogoMark({ size = 40, variant = "default", className = "" }) {
  const id = React.useId();
  if (variant === "solid-emerald") {
    return (
      <svg width={size} height={size} viewBox="0 0 40 40" className={className} aria-label="SwiftCash">
        <defs>
          <linearGradient id={`${id}-em`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#12CB8E" />
            <stop offset="1" stopColor="#059569" />
          </linearGradient>
        </defs>
        <rect width="40" height="40" rx="10" fill={`url(#${id}-em)`} />
        <path
          d="M27.5 13.5 C 21 13.5, 13 13, 13 17 C 13 21, 27 20, 27 24 C 27 28, 19.5 28, 12.5 27"
          fill="none" stroke="#FFFFFF" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"
        />
      </svg>
    );
  }
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" className={className} aria-label="SwiftCash">
      <defs>
        <linearGradient id={`${id}-bg`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#103C58" />
          <stop offset="1" stopColor="#0A2A3E" />
        </linearGradient>
        <linearGradient id={`${id}-s`} x1="0.15" y1="0.2" x2="0.85" y2="0.9">
          <stop offset="0" stopColor="#22D08E" />
          <stop offset="1" stopColor="#0AA469" />
        </linearGradient>
      </defs>
      <rect width="40" height="40" rx="10" fill={`url(#${id}-bg)`} />
      {/* subtle inner border for depth */}
      <rect x="0.5" y="0.5" width="39" height="39" rx="9.5" fill="none" stroke="#12CB8E" strokeOpacity="0.18" />
      {/* signature emerald S — a smooth cubic bezier */}
      <path
        d="M27.5 13.5 C 21 13.5, 13 13, 13 17 C 13 21, 27 20, 27 24 C 27 28, 19.5 28, 12.5 27"
        fill="none" stroke={`url(#${id}-s)`} strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"
      />
      {/* accent glow dot bottom-right for identity */}
      <circle cx="31" cy="30" r="1.3" fill="#22D08E" opacity="0.75" />
    </svg>
  );
}

/**
 * Wordmark + mark combined lockup.
 * <Logo /> replaces the old inline `<div>S</div>` + text pattern site-wide.
 */
export default function Logo({ size = 36, tone = "auto", className = "" }) {
  // tone="light" → white text (for dark backgrounds)
  // tone="dark"  → navy text (for light backgrounds)
  // tone="auto"  → inherit
  const textColor = tone === "light" ? "text-white" : tone === "dark" ? "text-navy" : "";
  return (
    <div className={`inline-flex items-center gap-2.5 ${className}`}>
      <LogoMark size={size} />
      <div className={`font-display font-extrabold tracking-[-0.035em] leading-none ${textColor}`} style={{ fontSize: size * 0.55 }}>
        Swift<span className="text-emerald">Cash</span>
      </div>
    </div>
  );
}
